/**
 * 小红书爆款 - 数据可视化模板
 *
 * 视觉结构：
 * - 开场钩子（3秒）：计数器数字 + 震撼标签
 * - 主体内容（15秒）：环形图 + 水平进度条 + 趋势卡片（竖版布局）
 * - 结尾CTA（2秒）：结论 + CTA
 *
 * 视觉元素：
 * - 配色：理性专业（蓝绿渐变 + 深色底）
 * - 动画：数字滚动、进度条增长、环形绘制
 * - 背景：科技感网格 + 数据粒子流
 * - 布局：1080×1920 竖版优化
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
} from 'remotion';

const DATA_COLORS = {
  primary: {
    blue: '#3B82F6',
    teal: '#06B6D4',
    green: '#22C55E',
    purple: '#A855F7',
    indigo: '#6366F1',
  },
  secondary: {
    lightBlue: '#60A5FA',
    cyan: '#22D3EE',
    lime: '#84CC16',
    pink: '#EC4899',
    amber: '#F59E0B',
  },
  gradients: {
    cool: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 50%, #22C55E 100%)',
    tech: 'linear-gradient(180deg, #020617 0%, #0F172A 50%, #1E293B 100%)',
    data: 'linear-gradient(135deg, #3B82F6 0%, #A855F7 100%)',
    blueTeal: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
  },
  white: '#FFFFFF',
  black: '#020617',
  gray: '#94A3B8',
  darkGray: '#334155',
  surface: 'rgba(255, 255, 255, 0.06)',
};

// ============================================
// 📊 科技感数据流背景
// ============================================
const DataFlowBackground: React.FC<{
  count?: number;
}> = ({ count = 25 }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const dataStreams = Array.from({ length: count }, (_, i) => {
    const x = (i * 43.7) % width;
    const baseY = (i * 73.3) % height;
    const speed = 0.8 + (i % 3) * 0.4;
    const y = (baseY + frame * speed) % height;
    const opacity = 0.06 + Math.sin(frame * 0.015 + i) * 0.03;
    const length = 80 + (i % 5) * 40;
    return { x, y, opacity, length };
  });

  const pulseOpacity = 0.05 + Math.sin(frame * 0.03) * 0.02;

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: DATA_COLORS.gradients.tech,
        }}
      />

      {/* 网格 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(${DATA_COLORS.primary.blue}12 1px, transparent 1px),
            linear-gradient(90deg, ${DATA_COLORS.primary.blue}12 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          opacity: pulseOpacity * 10,
        }}
      />

      {/* 顶部光晕 */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          left: '50%',
          marginLeft: -300,
          width: 600,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${DATA_COLORS.primary.blue}15 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />

      {/* 数据流 */}
      {dataStreams.map((stream, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: stream.x,
            top: stream.y,
            width: 2,
            height: stream.length,
            background: `linear-gradient(to bottom, transparent, ${DATA_COLORS.primary.blue}${Math.floor(stream.opacity * 255).toString(16).padStart(2, '0')}, transparent)`,
            borderRadius: 1,
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 开场钩子 - 数字计数器（3秒）
// ============================================
const DataHookScene: React.FC<{
  mainNumber: string;
  label: string;
  subtext?: string;
}> = ({ mainNumber, label, subtext }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const numberProgress = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 180, mass: 0.5 },
  });

  const numberScale = interpolate(numberProgress, [0, 1], [0.2, 1]);

  const labelProgress = spring({
    frame: frame - 18,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const glow = 0.3 + Math.sin(frame * 0.08) * 0.15;
  const lineWidth = interpolate(numberProgress, [0, 1], [0, 320]);

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 64,
      }}
    >
      {/* 光晕圈 */}
      <div
        style={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${DATA_COLORS.primary.blue}${Math.floor(glow * 20).toString(16).padStart(2, '0')} 0%, transparent 70%)`,
        }}
      />

      {/* 主数字 */}
      <div
        style={{
          fontSize: 160,
          fontWeight: 900,
          background: DATA_COLORS.gradients.cool,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          lineHeight: 1,
          transform: `scale(${numberScale})`,
          marginBottom: 24,
          letterSpacing: -2,
        }}
      >
        {mainNumber}
      </div>

      {/* 分隔线 */}
      <div
        style={{
          width: lineWidth,
          height: 3,
          background: DATA_COLORS.gradients.blueTeal,
          borderRadius: 2,
          marginBottom: 28,
          boxShadow: `0 0 12px ${DATA_COLORS.primary.blue}40`,
        }}
      />

      {/* 标签 */}
      <div
        style={{
          fontSize: 60,
          fontWeight: 700,
          color: DATA_COLORS.white,
          textAlign: 'center',
          opacity: labelProgress,
          transform: `translateY(${interpolate(labelProgress, [0, 1], [24, 0])}px)`,
          marginBottom: 16,
          letterSpacing: 1,
        }}
      >
        {label}
      </div>

      {subtext && (
        <div
          style={{
            fontSize: 50,
            fontWeight: 600,
            color: DATA_COLORS.gray,
            textAlign: 'center',
            opacity: labelProgress,
          }}
        >
          {subtext}
        </div>
      )}
    </AbsoluteFill>
  );
};

// ============================================
// 📊 环形图组件（SVG）
// ============================================
const DonutChart: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
  delay: number;
}> = ({ data, delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 20, stiffness: 80 },
  });

  const total = data.reduce((sum, d) => sum + d.value, 0);
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 120;
  const strokeWidth = 36;

  let cumulativeAngle = -90;

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((item, i) => {
          const angle = (item.value / total) * 360 * progress;
          const startAngle = cumulativeAngle;
          cumulativeAngle += angle;
          const endAngle = startAngle + angle;

          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;

          const x1 = cx + radius * Math.cos(startRad);
          const y1 = cy + radius * Math.sin(startRad);
          const x2 = cx + radius * Math.cos(endRad);
          const y2 = cy + radius * Math.sin(endRad);

          const largeArc = angle > 180 ? 1 : 0;

          if (angle < 0.5) return null;

          const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;

          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              opacity={progress}
            />
          );
        })}

        {/* 中心文字 */}
        <text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          fill={DATA_COLORS.white}
          fontSize={60}
          fontWeight={900}
          opacity={progress}
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 56}
          textAnchor="middle"
          fill={DATA_COLORS.gray}
          fontSize={40}
          fontWeight={600}
          opacity={progress}
        >
          总计
        </text>
      </svg>
    </div>
  );
};

// ============================================
// 📊 水平进度条
// ============================================
const BarChart: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
  delay: number;
}> = ({ data, delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const maxValue = Math.max(...data.map((d) => d.value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {data.map((item, i) => {
        const itemDelay = delay + i * 8;
        const progress = spring({
          frame: frame - itemDelay,
          fps,
          config: { damping: 16, stiffness: 110 },
        });

        const barWidth = interpolate(
          progress,
          [0, 1],
          [0, (item.value / maxValue) * 100],
        );

        return (
          <div key={i}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 25,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    background: item.color,
                    boxShadow: `0 0 8px ${item.color}50`,
                  }}
                />
                <span
                  style={{
                    fontSize: 40,
                    fontWeight: 600,
                    color: DATA_COLORS.white,
                    opacity: progress,
                  }}
                >
                  {item.label}
                </span>
              </div>
              <span
                style={{
                  fontSize: 40,
                  fontWeight: 800,
                  color: item.color,
                  opacity: progress,
                }}
              >
                {Math.round(item.value * progress)}
              </span>
            </div>

            <div
              style={{
                width: '100%',
                height: 16,
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${barWidth}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}BB 100%)`,
                  borderRadius: 8,
                  boxShadow: `0 2px 12px ${item.color}40`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// 📈 趋势卡片
// ============================================
const TrendCard: React.FC<{
  title: string;
  currentValue: string;
  change: string;
  trend: 'up' | 'down';
  delay: number;
}> = ({ title, currentValue, change, trend, delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const translateY = interpolate(progress, [0, 1], [40, 0]);
  const trendColor =
    trend === 'up' ? DATA_COLORS.primary.green : DATA_COLORS.secondary.pink;
  const trendIcon = trend === 'up' ? '↑' : '↓';

  return (
    <div
      style={{
        flex: 1,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(16px)',
        borderRadius: 20,
        padding: '24px 28px',
        border: `1.5px solid ${trendColor}30`,
        opacity: progress,
        transform: `translateY(${translateY}px)`,
        boxShadow: `0 4px 20px ${trendColor}10`,
      }}
    >
      <div
        style={{
          fontSize: 40,
          fontWeight: 600,
          color: DATA_COLORS.gray,
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 52,
          fontWeight: 900,
          color: DATA_COLORS.white,
          marginBottom: 10,
          letterSpacing: -1,
        }}
      >
        {currentValue}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 46,
            fontWeight: 800,
            color: trendColor,
          }}
        >
          {trendIcon} {change}
        </span>
      </div>
    </div>
  );
};

// ============================================
// 🎬 主体数据场景（竖版布局）
// ============================================
const DataAnalysisScene: React.FC<{
  chartData: Array<{ label: string; value: number; color: string }>;
  trends: Array<{
    title: string;
    currentValue: string;
    change: string;
    trend: 'up' | 'down';
  }>;
}> = ({ chartData, trends }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 180 },
  });

  return (
    <AbsoluteFill>
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 48,
          right: 48,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 60,
            fontWeight: 700,
            color: DATA_COLORS.white,
            opacity: titleProgress,
            transform: `translateY(${interpolate(titleProgress, [0, 1], [-20, 0])}px)`,
            letterSpacing: 1,
          }}
        >
          📊 数据分析
        </div>
        <div
          style={{
            margin: '12px auto 0',
            width: interpolate(titleProgress, [0, 1], [0, 160]),
            height: 3,
            background: DATA_COLORS.gradients.blueTeal,
            borderRadius: 2,
          }}
        />
      </div>

      {/* 环形图 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 48,
          right: 48,
        }}
      >
        <DonutChart data={chartData} delay={15} />
      </div>

      {/* 进度条 */}
      <div
        style={{
          position: 'absolute',
          top: 650,
          left: 48,
          right: 48,
        }}
      >
        <BarChart data={chartData} delay={25} />
      </div>

      {/* 趋势卡片 - 横向排列 */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 48,
          right: 48,
          display: 'flex',
          gap: 20,
        }}
      >
        {trends.map((trend, i) => (
          <TrendCard key={i} {...trend} delay={50 + i * 12} />
        ))}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 结尾CTA场景
// ============================================
const DataCTAScene: React.FC<{
  conclusion: string;
  actionText: string;
}> = ({ conclusion, actionText }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 180, mass: 0.5 },
  });

  const scale = interpolate(progress, [0, 1], [0.4, 1]);

  const lineWidth = interpolate(progress, [0, 1], [0, 240]);

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 结论 */}
      <div
        style={{
          fontSize: 72,
          fontWeight: 800,
          background: DATA_COLORS.gradients.cool,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          transform: `scale(${scale})`,
          marginBottom: 20,
          letterSpacing: 2,
        }}
      >
        {conclusion}
      </div>

      {/* 分隔线 */}
      <div
        style={{
          width: lineWidth,
          height: 3,
          background: DATA_COLORS.gradients.blueTeal,
          borderRadius: 2,
          marginBottom: 48,
        }}
      />

      {/* 行动按钮 */}
      <div
        style={{
          background: DATA_COLORS.gradients.cool,
          padding: '24px 64px',
          borderRadius: 60,
          fontSize: 44,
          fontWeight: 800,
          color: DATA_COLORS.white,
          opacity: progress,
          transform: `scale(${interpolate(progress, [0, 1], [0.6, 1])})`,
          boxShadow: `0 8px 32px ${DATA_COLORS.primary.blue}30`,
          letterSpacing: 1,
        }}
      >
        {actionText}
      </div>

      {/* 装饰 */}
      <div
        style={{
          position: 'absolute',
          bottom: 180,
          display: 'flex',
          gap: 20,
        }}
      >
        {['📊', '👍', '💬'].map((emoji, i) => {
          const emojiProgress = spring({
            frame: frame - (25 + i * 10),
            fps,
            config: { damping: 16, stiffness: 110 },
          });
          const float = Math.sin(frame * 0.1 + i * 2) * 8;
          return (
            <div
              key={i}
              style={{
                fontSize: 52,
                opacity: emojiProgress,
                transform: `translateY(${interpolate(emojiProgress, [0, 1], [30, 0]) + float}px)`,
              }}
            >
              {emoji}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 完整数据可视化模板
// ============================================
export const XHSDataVisualization: React.FC<{
  hookNumber: string;
  hookLabel: string;
  hookSubtext?: string;
  chartData: Array<{ label: string; value: number; color: string }>;
  trends: Array<{
    title: string;
    currentValue: string;
    change: string;
    trend: 'up' | 'down';
  }>;
  conclusion: string;
  actionText: string;
}> = ({
  hookNumber,
  hookLabel,
  hookSubtext,
  chartData,
  trends,
  conclusion,
  actionText,
}) => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      <DataFlowBackground count={30} />

      <Sequence from={0} durationInFrames={fps * 3}>
        <DataHookScene
          mainNumber={hookNumber}
          label={hookLabel}
          subtext={hookSubtext}
        />
      </Sequence>

      <Sequence from={fps * 3} durationInFrames={fps * 15}>
        <DataAnalysisScene chartData={chartData} trends={trends} />
      </Sequence>

      <Sequence from={fps * 18} durationInFrames={fps * 2}>
        <DataCTAScene conclusion={conclusion} actionText={actionText} />
      </Sequence>
    </AbsoluteFill>
  );
};
